import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, Upload, Download, Trash2, Plus, Gavel, FileText,
  Bell, Brain, Sparkles, Scale, Shield, Mic, MessageSquare, Calendar, MapPin, Clock, FileDown
} from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "@/components/Markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  "France", "Bénin", "Côte d'Ivoire", "Sénégal", "Togo", "Cameroun", "Mali",
  "Burkina Faso", "Niger", "Gabon", "Congo (RDC)", "Congo (Brazzaville)", "Guinée",
  "Tchad", "OHADA (espace)", "Belgique", "Suisse", "Luxembourg", "Canada (Québec)",
  "Maroc", "Tunisie", "Algérie", "Madagascar", "Autre"
];

const statusColors = {
  ouvert: "bg-green-100 text-green-700", en_cours: "bg-blue-100 text-blue-700",
  suspendu: "bg-amber-100 text-amber-700", ferme: "bg-slate-100 text-slate-600"
};
const statusLabels = { ouvert: "Ouvert", en_cours: "En cours", suspendu: "Suspendu", ferme: "Fermé" };
const stepTypeLabels = { audience: "Audience", demarche: "Démarche", echeance: "Échéance" };
const stepTypeColors = { audience: "bg-purple-100 text-purple-700", demarche: "bg-blue-100 text-blue-700", echeance: "bg-amber-100 text-amber-700" };

const aiModes = [
  { key: "analysis", label: "Analyse", icon: Brain },
  { key: "strategy", label: "Stratégie", icon: Sparkles },
  { key: "defense", label: "Ligne de défense", icon: Shield },
  { key: "plaidoirie", label: "Plaidoirie", icon: Mic }
];
const aiModeLabels = { analysis: "Analyse juridique", strategy: "Stratégie", defense: "Ligne de défense", plaidoirie: "Plaidoirie" };

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}
function fmtSize(b) {
  if (!b) return "";
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(1) + " MB";
}

export default function CaseDetail({ user, setUser }) {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [steps, setSteps] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [aiOutputs, setAiOutputs] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const [stepDialog, setStepDialog] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [c, d, s, r, a] = await Promise.all([
        fetch(`${API_URL}/api/cases/${caseId}`, { credentials: "include" }),
        fetch(`${API_URL}/api/cases/${caseId}/documents`, { credentials: "include" }),
        fetch(`${API_URL}/api/cases/${caseId}/steps`, { credentials: "include" }),
        fetch(`${API_URL}/api/cases/${caseId}/reminders`, { credentials: "include" }),
        fetch(`${API_URL}/api/cases/${caseId}/ai`, { credentials: "include" })
      ]);
      if (c.ok) setCaseData(await c.json());
      if (d.ok) setDocs(await d.json());
      if (s.ok) setSteps(await s.json());
      if (r.ok) setReminders(await r.json());
      if (a.ok) setAiOutputs(await a.json());
    } catch (e) { toast.error("Erreur de chargement"); }
    setLoading(false);
  }, [caseId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function saveField(field, value) {
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value })
      });
      if (res.ok) { setCaseData(await res.json()); toast.success("Enregistré"); }
    } catch (e) { toast.error("Erreur"); }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("piece_type", "piece");
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/upload`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) { toast.success("Pièce ajoutée"); fetchAll(); }
      else toast.error("Erreur d'upload");
    } catch (e) { toast.error("Erreur d'upload"); }
    e.target.value = "";
    setUploading(false);
  }

  async function downloadDoc(doc) {
    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.document_id}/download`, { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = doc.original_filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error("Erreur"); }
  }

  async function addNote() {
    if (!noteText.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/notes`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: noteText })
      });
      if (res.ok) { setNoteText(""); fetchAll(); toast.success("Note ajoutée"); }
    } catch (e) { toast.error("Erreur"); }
  }

  async function deleteStep(stepId) {
    if (!window.confirm("Supprimer cette étape ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/steps/${stepId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Étape supprimée"); fetchAll(); }
    } catch (e) { toast.error("Erreur"); }
  }

  async function runAI(mode) {
    setAiLoading(mode);
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/ai`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode })
      });
      if (res.ok) {
        const rec = await res.json();
        toast.success("Analyse lancée — génération en cours...");
        await fetchAll();
        pollOutput(rec.output_id);
      } else {
        const e = await res.json();
        toast.error(typeof e.detail === "string" ? e.detail : "Erreur IA");
        setAiLoading("");
      }
    } catch (e) {
      toast.error("Erreur IA");
      setAiLoading("");
    }
  }

  function pollOutput(outputId) {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/cases/${caseId}/ai`, { credentials: "include" });
        if (res.ok) {
          const list = await res.json();
          setAiOutputs(list);
          const o = list.find((x) => x.output_id === outputId);
          if (o && o.status !== "processing") {
            clearInterval(interval);
            setAiLoading("");
            if (o.status === "failed") toast.error("Échec de l'analyse IA");
            else toast.success("Analyse IA terminée");
          }
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    setTimeout(() => { clearInterval(interval); setAiLoading(""); }, 180000);
  }

  async function deleteAI(outputId) {
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/ai/${outputId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { fetchAll(); toast.success("Supprimé"); }
    } catch (e) { toast.error("Erreur"); }
  }

  async function exportAI(outputId, format) {
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/ai/${outputId}/export?format=${format}`, { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `document.${format}`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error("Erreur export"); }
  }

  if (loading) {
    return <AppLayout user={user} setUser={setUser}><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div></AppLayout>;
  }
  if (!caseData) {
    return <AppLayout user={user} setUser={setUser}><div className="p-8">Dossier introuvable.</div></AppLayout>;
  }

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="case-detail-page">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/cases")} data-testid="back-to-cases">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux dossiers
        </Button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-semibold tracking-tight">{caseData.title}</h1>
              <span className="text-sm font-mono text-muted-foreground">{caseData.reference}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={statusColors[caseData.status]}>{statusLabels[caseData.status]}</Badge>
              {caseData.country && <Badge variant="outline" className="gap-1"><Scale className="w-3 h-3" />{caseData.country}</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Statut</Label>
            <Select value={caseData.status} onValueChange={(v) => saveField("status", v)}>
              <SelectTrigger className="w-36" data-testid="case-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ouvert">Ouvert</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="suspendu">Suspendu</SelectItem>
                <SelectItem value="ferme">Fermé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Aperçu</TabsTrigger>
            <TabsTrigger value="pieces" data-testid="tab-pieces">Pièces ({docs.length})</TabsTrigger>
            <TabsTrigger value="procedure" data-testid="tab-procedure">Procédure ({steps.length})</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">Rappels ({reminders.length})</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">Assistant IA</TabsTrigger>
          </TabsList>

          {/* ===== Overview ===== */}
          <TabsContent value="overview" className="space-y-6">
            <Card><CardContent className="pt-6 grid md:grid-cols-2 gap-4">
              <EditableField label="Pays / droit applicable" value={caseData.country} onSave={(v) => saveField("country", v)} type="country" />
              <EditableField label="Juridiction / tribunal" value={caseData.jurisdiction} onSave={(v) => saveField("jurisdiction", v)} />
              <EditableField label="Demandeur" value={caseData.plaintiff} onSave={(v) => saveField("plaintiff", v)} />
              <EditableField label="Défendeur" value={caseData.defendant} onSave={(v) => saveField("defendant", v)} />
              <EditableField label="Juge" value={caseData.judge} onSave={(v) => saveField("judge", v)} />
              <EditableField label="Avocat adverse" value={caseData.opposing_counsel} onSave={(v) => saveField("opposing_counsel", v)} />
            </CardContent></Card>

            {caseData.description && (
              <Card><CardContent className="pt-6">
                <p className="text-xs text-muted-foreground mb-1">Description / faits</p>
                <p className="text-sm whitespace-pre-wrap">{caseData.description}</p>
              </CardContent></Card>
            )}

            <Card><CardContent className="pt-6">
              <p className="text-sm font-medium mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Notes ({(caseData.notes || []).length})</p>
              <div className="space-y-2 mb-3">
                {(caseData.notes || []).map((n) => (
                  <div key={n.note_id} className="p-3 bg-slate-50 rounded-md border border-slate-100">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{n.author}</span>
                      <span className="text-xs text-muted-foreground">{new Date(n.timestamp).toLocaleString("fr-FR")}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{n.content}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Ajouter une note..." rows={2} className="flex-1" data-testid="case-note-input" />
                <Button size="sm" className="self-end" onClick={addNote} disabled={!noteText.trim()} data-testid="add-note-btn">Ajouter</Button>
              </div>
            </CardContent></Card>
          </TabsContent>

          {/* ===== Pieces ===== */}
          <TabsContent value="pieces" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Pièces justificatives du dossier</p>
              <label>
                <input type="file" className="hidden" onChange={handleUpload} data-testid="case-upload-input"
                  accept=".pdf,.docx,.doc,.xlsx,.csv,.txt,.jpg,.jpeg,.png" />
                <Button asChild disabled={uploading} data-testid="case-upload-btn">
                  <span>{uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Ajouter une pièce</span>
                </Button>
              </label>
            </div>
            {docs.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Aucune pièce. Ajoutez les documents du dossier.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.document_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={"piece-" + d.document_id}>
                    <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-muted-foreground">{fmtSize(d.size)} · {fmtDate(d.created_at)}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadDoc(d)}><Download className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== Procedure / steps ===== */}
          <TabsContent value="procedure" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Audiences, démarches et échéances</p>
              <Button onClick={() => setStepDialog(true)} data-testid="add-step-btn"><Plus className="w-4 h-4 mr-2" />Ajouter une étape</Button>
            </div>
            {steps.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Aucune étape enregistrée.</CardContent></Card>
            ) : (
              <div className="space-y-3">
                {steps.map((s) => (
                  <div key={s.step_id} className="p-4 rounded-lg border border-slate-200 bg-white" data-testid={"step-" + s.step_id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className={stepTypeColors[s.step_type] || "bg-slate-100"}>{stepTypeLabels[s.step_type] || s.step_type}</Badge>
                          <span className="text-sm font-medium">{s.title}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(s.date)}</span>
                          {s.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</span>}
                          {(s.reminder_offsets || []).length > 0 && <span className="flex items-center gap-1"><Bell className="w-3 h-3" />{s.reminder_offsets.join(", ")}j avant</span>}
                        </div>
                        {s.clerk_notes && <p className="text-sm mt-2"><span className="text-xs text-muted-foreground">Notes du greffier : </span>{s.clerk_notes}</p>}
                        {s.decision && <p className="text-sm mt-1"><span className="text-xs text-muted-foreground">Décision : </span>{s.decision}</p>}
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteStep(s.step_id)} data-testid={"delete-step-" + s.step_id}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== Reminders ===== */}
          <TabsContent value="reminders" className="space-y-4">
            <p className="text-sm text-muted-foreground">Rappels programmés (envoi par email + notification à l'échéance)</p>
            {reminders.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Aucun rappel. Ajoutez des étapes avec des rappels dans l'onglet Procédure.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {reminders.map((r) => (
                  <div key={r.reminder_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={"reminder-" + r.reminder_id}>
                    <Bell className={"w-4 h-4 " + (r.sent ? "text-slate-300" : "text-amber-500")} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {r.days_before}j avant · déclenchement : {fmtDate(r.due_at)}
                      </p>
                    </div>
                    <Badge variant={r.sent ? "secondary" : "outline"}>{r.sent ? "Envoyé" : "Programmé"}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== AI ===== */}
          <TabsContent value="ai" className="space-y-4">
            <Card><CardContent className="pt-6">
              <p className="text-sm font-medium mb-1">Assistant juridique IA (Claude Opus 4.5)</p>
              <p className="text-xs text-muted-foreground mb-4">L'IA analyse toutes les pièces, audiences et informations du dossier et cite les lois, articles et jurisprudence du pays concerné.</p>
              <div className="flex flex-wrap gap-2">
                {aiModes.map((m) => {
                  const Icon = m.icon;
                  return (
                    <Button key={m.key} variant="outline" onClick={() => runAI(m.key)} disabled={!!aiLoading} data-testid={"ai-" + m.key + "-btn"}>
                      {aiLoading === m.key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2" />}
                      {m.label}
                    </Button>
                  );
                })}
              </div>
              {aiLoading && <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Génération en cours (cela peut prendre jusqu'à une minute)...</p>}
            </CardContent></Card>

            {aiOutputs.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Aucune analyse générée pour l'instant.</CardContent></Card>
            ) : (
              <div className="space-y-4">
                {aiOutputs.map((o) => (
                  <Card key={o.output_id} data-testid={"ai-output-" + o.output_id}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700">{aiModeLabels[o.mode] || o.mode}</Badge>
                          <span className="text-xs text-muted-foreground">{o.country} · {new Date(o.created_at).toLocaleString("fr-FR")}</span>
                          {o.status === "processing" && <Badge variant="outline" className="gap-1"><Loader2 className="w-3 h-3 animate-spin" />En cours</Badge>}
                          {o.status === "failed" && <Badge variant="destructive">Échec</Badge>}
                        </div>
                        {o.status !== "processing" && (
                          <div className="flex items-center gap-1">
                            {o.status !== "failed" && (
                              <>
                                <Button variant="outline" size="sm" onClick={() => exportAI(o.output_id, "docx")} data-testid={"export-docx-" + o.output_id}><FileDown className="w-4 h-4 mr-1" />Word</Button>
                                <Button variant="outline" size="sm" onClick={() => exportAI(o.output_id, "pdf")} data-testid={"export-pdf-" + o.output_id}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteAI(o.output_id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </div>
                      {o.status === "processing" ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                          <Loader2 className="w-4 h-4 animate-spin" /> Génération en cours (cela peut prendre jusqu'à une minute)...
                        </div>
                      ) : o.status === "failed" ? (
                        <p className="text-sm text-destructive">Une erreur est survenue : {o.error || "réessayez"}.</p>
                      ) : (
                        <div className="bg-slate-50 rounded-md p-4 max-h-[500px] overflow-y-auto border border-slate-100"><Markdown content={o.result} /></div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <StepDialog open={stepDialog} onClose={() => setStepDialog(false)} caseId={caseId} onSaved={fetchAll} />
      </div>
    </AppLayout>
  );
}

function EditableField({ label, value, onSave, type }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  function commit() { setEditing(false); if (val !== (value || "")) onSave(val); }
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {editing ? (
        type === "country" ? (
          <Select value={val || "none"} onValueChange={(v) => { const nv = v === "none" ? "" : v; setVal(nv); setEditing(false); if (nv !== (value || "")) onSave(nv); }}>
            <SelectTrigger className="h-8" data-testid={"field-" + label}><SelectValue placeholder="Choisir..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Non précisé</SelectItem>
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); }} className="h-8" data-testid={"field-" + label} />
        )
      ) : (
        <p className="text-sm cursor-pointer hover:text-blue-600 min-h-[20px]" onClick={() => setEditing(true)}>{value || <span className="text-slate-400 italic">Cliquer pour renseigner</span>}</p>
      )}
    </div>
  );
}

function StepDialog({ open, onClose, caseId, onSaved }) {
  const [form, setForm] = useState({ step_type: "audience", title: "", date: "", location: "", clerk_notes: "", decision: "", reminders: "30, 7, 1" });
  const [saving, setSaving] = useState(false);

  function upd(f, v) { setForm((p) => ({ ...p, [f]: v })); }

  async function submit() {
    if (!form.title.trim() || !form.date) { toast.error("Titre et date requis"); return; }
    const offsets = form.reminders.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n) && n >= 0);
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/steps`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step_type: form.step_type, title: form.title, date: form.date,
          location: form.location, clerk_notes: form.clerk_notes, decision: form.decision,
          reminder_offsets: offsets
        })
      });
      if (res.ok) { toast.success("Étape ajoutée"); onSaved(); onClose(); setForm({ step_type: "audience", title: "", date: "", location: "", clerk_notes: "", decision: "", reminders: "30, 7, 1" }); }
      else toast.error("Erreur");
    } catch (e) { toast.error("Erreur"); }
    setSaving(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="step-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter une étape</DialogTitle>
          <DialogDescription>Audience, démarche ou échéance avec rappels programmés</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.step_type} onValueChange={(v) => upd("step_type", v)}>
                <SelectTrigger data-testid="step-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="audience">Audience</SelectItem>
                  <SelectItem value="demarche">Démarche</SelectItem>
                  <SelectItem value="echeance">Échéance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => upd("date", e.target.value)} data-testid="step-date-input" />
            </div>
          </div>
          <div>
            <Label>Intitulé</Label>
            <Input value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="Ex: Audience de mise en état" data-testid="step-title-input" />
          </div>
          <div>
            <Label>Lieu (optionnel)</Label>
            <Input value={form.location} onChange={(e) => upd("location", e.target.value)} placeholder="Tribunal de..." />
          </div>
          <div>
            <Label>Notes du greffier (optionnel)</Label>
            <Textarea value={form.clerk_notes} onChange={(e) => upd("clerk_notes", e.target.value)} rows={2} data-testid="step-clerk-notes" />
          </div>
          <div>
            <Label>Décision / résultat (optionnel)</Label>
            <Input value={form.decision} onChange={(e) => upd("decision", e.target.value)} />
          </div>
          <div>
            <Label>Rappels (jours avant, séparés par des virgules)</Label>
            <Input value={form.reminders} onChange={(e) => upd("reminders", e.target.value)} placeholder="Ex: 30, 7, 1" data-testid="step-reminders-input" />
            <p className="text-xs text-muted-foreground mt-1">Un rappel (email + notification) sera envoyé le nombre de jours indiqué avant la date.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-step-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Ajouter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
